import { v4 as uuidv4 } from 'uuid'

import { getActiveRegionId } from '@sportworld/js-sdk/src/actions/region'
import { eventCategory } from '@sportworld/js-sdk/src/constants/analytics'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { ChannelType } from '@sportworld/js-sdk/src/types/servicesTypes/configTypes'

import { webosKeys } from '@/constants/keys'
import { StorageIds } from '@/constants/storage'
import {
  isWebOSVersionHigherOrEqual,
  sendEventToGTAG,
  showInfo,
} from '@/utils/utils'

const webOS = (window as { [key: string]: any })['webOS'] as any

const setPlatformUuid = (uuid: string) => {
  localStorage.setItem(StorageIds.platformUuid, uuid)
}
const getPlatformUuid = () => {
  const platformUuid = localStorage.getItem(StorageIds.platformUuid) || ''
  return platformUuid
}
export default class WebosPlatform {
  name: string
  appBundle: string
  keys: { [key: string]: number }
  duid: string
  uuid: string
  modelCode: string
  modelName: string
  modelYear: string
  version: string
  totalMemory: string
  widgetVersion: string | undefined
  constructor() {
    this.name = 'WebOS'
    this.appBundle = '1233595'
    this.keys = webosKeys
    this.duid = ''
    this.uuid = ''
    this.modelCode = ''
    this.modelName = ''
    this.modelYear = ''
    this.version = '0'
    this.totalMemory = '0'
    this.widgetVersion = undefined
    this.init()
  }
  init() {
    isDevMode && console.log('[platform] init', this.name)
    this.setDuid()
    this.setUuid()
    this.setModelProps()
    this.setWidgetVersion()
    this.setTotalMemory()
  }
  setDuid() {
    webOS?.service?.request('luna://com.webos.service.sm', {
      method: 'deviceid/getIDs',
      parameters: {
        idType: ['LGUDID'],
      },
      onSuccess: (response: { [key: string]: any }) => {
        const udid = response.idList[0].idValue
        this.duid = udid
      },
      onFailure: function () {
        isDevMode && console.log('Failed to get system ID information')
      },
    })
  }
  setUuid() {
    const uuid = getPlatformUuid()
    if (uuid) {
      this.uuid = uuid
    } else {
      const createdUuid = uuidv4()
      setPlatformUuid(createdUuid)
      this.uuid = createdUuid
    }
  }
  reloadApp() {
    return undefined
  }
  closeApp() {
    const closeAppWithWebOSPopup = isWebOSVersionHigherOrEqual('6.0')
    closeAppWithWebOSPopup ? webOS?.platformBack() : window.close()
  }
  setModelProps() {
    webOS?.service?.request('luna://com.webos.service.tv.systemproperty', {
      method: 'getSystemInfo',
      parameters: {
        keys: ['modelName', 'boardType', 'sdkVersion'],
      },
      onComplete: (response: { [key: string]: string }) => {
        const isSucceeded = response.returnValue
        if (isSucceeded) {
          this.modelCode = response.boardType
          this.modelName = response.modelName
          this.version = response.sdkVersion.split('.').slice(0, 2).join('.')
          const version = response.sdkVersion.split('.')[0]
          if (version === '7') {
            this.modelYear = '2022'
          } else if (version === '6') {
            this.modelYear = '2021'
          } else if (version === '5') {
            this.modelYear = '2020'
          } else if (version === '4') {
            this.modelYear = '2018'
          } else if (version === '3') {
            this.modelYear = '2016'
          } else if (version === '2') {
            this.modelYear = '2015'
          } else if (version === '1') {
            this.modelYear = '2014'
          }
        } else {
          console.error('Failed to get TV device information')
        }
      },
    })
  }
  setWidgetVersion() {
    try {
      webOS?.fetchAppInfo((appInfo: any) => {
        const version = appInfo?.version
        this.widgetVersion = version
        isDevMode && console.log('Widget version:', version)
      })
    } catch (error) {
      console.error('Error getting widget version:', error)
    }
  }
  getDuid() {
    return this.duid
  }
  getUuid() {
    return this.uuid
  }
  getIfa() {
    return this.duid
  }
  /**
   * Checks whether the device has limited ad tracking.
   *
   * @returns boolean value value to indicate whether limited ad tracking is set.
   * - true: Limit Ad Tracking
   * - false: Not Limit Ad Tracking
   */
  getLat() {
    return false
  }
  getVersion() {
    return this.version
  }
  getWidgetVersion() {
    return this.widgetVersion
  }
  getModelCode() {
    return this.modelCode
  }
  getModelName() {
    return this.modelName
  }
  getModelYear() {
    return this.modelYear
  }
  getAppBundleName() {
    return this.appBundle
  }
  isChannelTunable() {
    return false
  }
  tuneToChannel(channel: ChannelType) {
    showInfo('Tuning to a channel is not supported yet')
    const channelName = channel?.name
    const locators = channel?.locators?.join(', ')
    const knownNames = channel?.knownNames?.join(', ')
    const region = getActiveRegionId()
    sendEventToGTAG(eventCategory.TUNE_CHANNEL, {
      name: channelName,
      status: 'error',
      region,
      locators,
      knownNames,
      message: 'Cannot tune to Live TV channel',
      platform: this.name,
    })
  }
  deepLinkToPromotion() {}
  deepLinkToAppId() {}
  setTotalMemory() {
    webOS?.deviceInfo((device: { [key: string]: string }) => {
      const ddrSize = +device.ddrSize.split('G')[0] * 1024
      this.totalMemory = ddrSize.toString()
    })
  }
  getTotalMemory() {
    return this.totalMemory
  }
  getAvailableMemory() {
    return undefined
  }
  openAppStorePage() {
    isDevMode && console.log('[webos] openAppStorePage')
    return undefined
  }
}
